// components/RenderCrashBoundary.tsx
import React from "react";
import type { ErrorInfo, ReactNode } from "react";
import { ApiError } from "./apiError";

type Props = {
  children: ReactNode;
  /** 에러 페이지 UI */
  fallback: (reset: () => void) => ReactNode;
  /** 라우트 변경 등에서 상태 초기화 */
  resetKeys?: unknown[];
  /** 로깅 훅(옵션) */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * 처리할지 말지 결정(기본: ApiError면 무시)
   * true => 에러 페이지로 전환, false => 통과(자식에 그대로 던짐)
   */
  shouldHandle?: (error: unknown, info: ErrorInfo) => boolean;
};

type State = { hasError: boolean; error?: Error };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(_: Error): State {
    // 일단 ErrorBoundary 모드 진입(렌더 차단)
    return { hasError: true, error: _ };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { onError, shouldHandle } = this.props;

    // 기본 정책: ApiError는 "API 오류"로 보고 렌더 크래시로 취급하지 않음
    const defaultShouldHandle = (err: unknown) => !(err instanceof ApiError);

    const handle = (shouldHandle ?? defaultShouldHandle)(error, info);

    console.log("[ErrorBoundary] 에러 발생:", error.name, error.message, "처리 여부:", handle);

    if (!handle) {
      // 처리하지 않을 거라면, 원래대로 표시되도록 상태를 복구
      // (주의: 이 경우 자식 쪽에서 에러가 다시 던져지면 또 boundary로 옵니다)
      // 다음 틱에서 복구(동일 렌더 사이클 반영 이슈 회피)
      console.log("[ErrorBoundary] 에러를 무시하고 상태 복구");
      queueMicrotask(() => this.setState({ hasError: false, error }));
      return;
    }

    console.log("[ErrorBoundary] 에러를 처리하고 ErrorPage 표시");
    onError?.(error, info);
  }

  componentDidUpdate(prevProps: Props) {
    // resetKeys가 바뀌면 에러 상태 초기화(라우팅/탭 변경 시 복구)
    if (JSON.stringify(prevProps.resetKeys) !== JSON.stringify(this.props.resetKeys)) {
      if (this.state.hasError) {
        this.setState({ hasError: false, error: undefined });
      }
    }
  }

  private reset = () => this.setState({ hasError: false, error: undefined });

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.reset);
    }
    return this.props.children;
  }
}
